/**
 * Live Search functionality for DecoLegno
 * Custom implementation with AJAX
 */

// SearchAPI handles all data fetching and AJAX communication
class SearchAPI {
    constructor() {
        if (typeof window.ajaxurl === 'undefined') {
            window.ajaxurl = '/wp/wp-admin/admin-ajax.php';
        }
    }

    performSearch(query) {
        const data = new FormData();
        data.append('action', 'decolegno_live_search');
        data.append('search_term', query);
        if (typeof decolegnoAjax !== 'undefined' && decolegnoAjax.nonce) {
            data.append('security', decolegnoAjax.nonce);
        }

        return this._fetchData(data);
    }

    getSuggestions(query) {
        const data = new FormData();
        data.append('action', 'decolegno_collection_suggestions');
        data.append('search_term', query);
        if (typeof decolegnoAjax !== 'undefined' && decolegnoAjax.nonce) {
            data.append('security', decolegnoAjax.nonce);
        }

        return this._fetchData(data);
    }

    _fetchData(formData) {
        return fetch(window.ajaxurl, {
            method: 'POST',
            credentials: 'same-origin',
            body: formData
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Network response was not ok: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                if (data.success) {
                    return data.data;
                } else {
                    throw new Error(data.data || 'Unknown error occurred');
                }
            });
    }
}

// ResultsRenderer handles all DOM manipulation and UI rendering
class ResultsRenderer {
    constructor(container) {
        this.container = container;
        this.currentQuery = '';
    }

    _renderViewAllLink(query, totalResults) {
        // Find or create the view-all container
        let viewAllContainer = document.querySelector('.js-live-search-view-all');
        if (!viewAllContainer) {
            viewAllContainer = document.createElement('div');
            viewAllContainer.className = 'c-live-search__view-all-container js-live-search-view-all';

            // Insert after the results container
            const resultsContainer = document.querySelector('.js-live-search-results');
            if (resultsContainer && resultsContainer.parentNode) {
                resultsContainer.parentNode.insertBefore(viewAllContainer, resultsContainer.nextSibling);
            }
        }

        // Clear existing content
        viewAllContainer.innerHTML = '';

        // Create the view-all link
        const viewAllLink = document.createElement('a');
        viewAllLink.className = 'c-live-search__view-all-link';
        viewAllLink.href = `${window.location.origin}/?s=${encodeURIComponent(query)}`;
        viewAllLink.innerHTML = `Alle&nbsp;<span>${totalResults}&nbsp;resultaten</span>&nbsp;bekijken`;

        viewAllContainer.appendChild(viewAllLink);
    }

    _clearViewAllLink() {
        const viewAllContainer = document.querySelector('.js-live-search-view-all');
        if (viewAllContainer) {
            viewAllContainer.innerHTML = '';
        }
    }

    clearResults() {
        if (this.container) {
            this.container.innerHTML = '';
        }
        this._clearViewAllLink();
    }

    renderNoResults(query) {
        const noResults = document.createElement('div');
        noResults.className = 'c-live-search__no-results';
        noResults.textContent = `Geen resultaten gevonden voor "${query}"`;
        this.container.appendChild(noResults);
    }

    renderSuggestion(suggestion) {
        // Remove any existing suggestion first to prevent duplicates
        this.removeSuggestion();

        const suggestionEl = document.createElement('div');
        suggestionEl.className = 'c-live-search__suggestion';
        suggestionEl.innerHTML = `<div>Bedoel je misschien <a href="#" class="c-live-search__suggestion-link" data-suggestion="${suggestion.name}">${suggestion.name}</a>?</div>`;

        setTimeout(() => {
            const suggestionLink = suggestionEl.querySelector('.c-live-search__suggestion-link');
            if (suggestionLink) {
                suggestionLink.addEventListener('click', (e) => {
                    e.preventDefault();
                    const searchInput = document.querySelector('.js-live-search-input');
                    if (searchInput) {
                        searchInput.value = suggestion.name;

                        if (window.liveSearchInstance) {
                            window.liveSearchInstance.search(suggestion.name);
                        }
                    }
                });
            }
        }, 0);

        if (this.container.firstChild) {
            this.container.insertBefore(suggestionEl, this.container.firstChild);
        } else {
            this.container.appendChild(suggestionEl);
        }
    }

    removeSuggestion() {
        const existingSuggestion = this.container.querySelector('.c-live-search__suggestion');
        if (existingSuggestion) {
            existingSuggestion.remove();
        }
    }

    renderResults(results, query) {
        this.currentQuery = query;
        this.totalResults = results.total;

        if (results.total === 0) {
            this.renderNoResults(query);
            return;
        }

        const resultsCount = document.createElement('div');
        resultsCount.className = 'c-live-search__results-count';
        resultsCount.innerHTML = `<p>${results.total} resultaten</p>`;
        this.container.appendChild(resultsCount);

        const contentContainer = this._createContentStructure();

        this._renderCategories(results.categories, contentContainer);

        this.container.appendChild(contentContainer);

        this._renderViewAllLink(query, results.total);
    }

    _createContentStructure() {
        const contentContainer = document.createElement('div');
        contentContainer.className = 'c-live-search__content-container';

        contentContainer.projectenBlogsWrapper = document.createElement('div');
        contentContainer.projectenBlogsWrapper.className = 'c-live-search__projecten-blogs-wrapper';

        return contentContainer;
    }

    _renderCategories(categories, contentContainer) {
        const allowed = ['decors', 'faqs', 'projecten', 'blogs_en_meer'];
        const categoriesWithResults = Object.entries(categories)
            .filter(([category, data]) => allowed.includes(category) && data.count > 0)
            .sort((a, b) => {
                const order = { 'decors': 1, 'faqs': 2, 'projecten': 3, 'blogs_en_meer': 4 };
                return order[a[0]] - order[b[0]];
            });

        const decorsCategory = categoriesWithResults.find(([category]) => category === 'decors');
        const faqsCategory = categoriesWithResults.find(([category]) => category === 'faqs');

        if (decorsCategory || faqsCategory) {
            const categorySection = decorsCategory
                ? this._createCategorySection(decorsCategory[0], decorsCategory[1])
                : this._createEmptyDecorsSection();

            if (faqsCategory) {
                const faqsItemsContainer = document.createElement('div');
                faqsItemsContainer.className = 'c-live-search__category__subsection__faqs-items';

                const faqsHeader = document.createElement('div');
                faqsHeader.className = 'c-live-search__category__header c-live-search__category__header--faqs';
                faqsHeader.innerHTML = `FAQ (${faqsCategory[1].count}) <a href="/over-ons/veelgestelde-vragen/" onclick="window.liveSearchInstance.closeSearchModal(); sessionStorage.setItem('faqSearchTerm', '${this.currentQuery.replace(/'/g, "\\'")}'); return true;">Alle FAQ</a>`;
                faqsItemsContainer.appendChild(faqsHeader);

                const faqsItems = faqsCategory[1].items
                faqsItems.forEach(item => {
                    const listItem = this._createItemElement('faqs', item);
                    faqsItemsContainer.appendChild(listItem);
                });

                categorySection.appendChild(faqsItemsContainer);
            }

            contentContainer.appendChild(categorySection);
        }

        // Combine projecten and blogs in one wrapper
        const projectenCategory = categoriesWithResults.find(([category]) => category === 'projecten');
        const blogsCategory = categoriesWithResults.find(([category]) => category === 'blogs_en_meer');

        if (projectenCategory || blogsCategory) {
            const combinedSection = this._createCombinedProjectenBlogsSection(
                projectenCategory ? projectenCategory[1] : null,
                blogsCategory ? blogsCategory[1] : null
            );
            contentContainer.appendChild(combinedSection);
        }
    }

    _createEmptyDecorsSection() {
        const categorySection = document.createElement('div');
        categorySection.className = 'c-live-search__category c-live-search__category--decors';

        const hiddenHeader = document.createElement('div');
        hiddenHeader.className = 'c-live-search__category__header';
        hiddenHeader.style.display = 'none';
        categorySection.appendChild(hiddenHeader);

        return categorySection;
    }

    _createCombinedProjectenBlogsSection(projectenData, blogsData) {
        const combinedSection = document.createElement('div');
        combinedSection.className = 'c-live-search__category c-live-search__category--projecten-blogs';

        // Add projecten subsection
        if (projectenData && projectenData.items.length > 0) {
            const projectenSubsection = document.createElement('div');
            projectenSubsection.className = 'c-live-search__category__subsection c-live-search__category__subsection--projecten';

            const projectenHeader = document.createElement('div');
            projectenHeader.className = 'c-live-search__category__header c-live-search__category__header--projecten';
            projectenHeader.innerHTML = `PROJECTEN (${projectenData.count}) <a href="/?searchtype=project&s=${encodeURIComponent(this.currentQuery)}">Alle projecten</a>`;
            projectenSubsection.appendChild(projectenHeader);

            const projectenItemsContainer = document.createElement('div');
            projectenItemsContainer.className = 'c-live-search__category__subsection__projecten-items';

            const projectenItems = projectenData.items.slice(0, 2);
            projectenItems.forEach(item => {
                const listItem = this._createItemElement('projecten', item);
                projectenItemsContainer.appendChild(listItem);
            });

            projectenSubsection.appendChild(projectenItemsContainer);
            combinedSection.appendChild(projectenSubsection);
        }

        // Add blogs subsection
        if (blogsData && blogsData.items.length > 0) {
            const blogsSubsection = document.createElement('div');
            blogsSubsection.className = 'c-live-search__category__subsection c-live-search__category__subsection--blogs';

            const blogsHeader = document.createElement('div');
            blogsHeader.className = 'c-live-search__category__header c-live-search__category__header--blogs';
            blogsHeader.innerHTML = `BLOGS & MEER (${blogsData.count}) <a href="/?searchtype=blog&s=${encodeURIComponent(this.currentQuery)}">Alle blogs en meer</a>`;
            blogsSubsection.appendChild(blogsHeader);

            const blogsItemsContainer = document.createElement('div');
            blogsItemsContainer.className = 'c-live-search__category__subsection__blogs-items';

            const blogsItems = blogsData.items.slice(0, 2);
            blogsItems.forEach(item => {
                const listItem = this._createItemElement('blogs_en_meer', item);
                blogsItemsContainer.appendChild(listItem);
            });

            blogsSubsection.appendChild(blogsItemsContainer);
            combinedSection.appendChild(blogsSubsection);
        }

        return combinedSection;
    }

    _getCategoryUrl(category) {
        const urls = {
            'decors': '/collectie/',
            'faqs': '/faq/',
            'projecten': '/projecten/',
            'blogs_en_meer': '/blog/'
        };

        return `${window.location.origin}${urls[category] || '/'}`;
    }

    _createCategorySection(category, data) {
        const categorySection = document.createElement('div');
        categorySection.className = `c-live-search__category c-live-search__category--${this._getCssClassCategory(category)}`;

        const categoryLabel = this._getCategoryLabel(category);

        const categoryHeader = document.createElement('div');
        categoryHeader.className = 'c-live-search__category__header';
        categoryHeader.innerHTML = `${categoryLabel} (${data.count}) <a href="/?searchtype=decor&s=${encodeURIComponent(this.currentQuery)}">Alle decors</a>`;
        categorySection.appendChild(categoryHeader);

        const itemsContainer = document.createElement('div');
        itemsContainer.className = `c-live-search__items c-live-search__category__${this._getCssClassCategory(category)}-items`;

        let items = data.items;
        if (category === 'projecten' || category === 'blogs_en_meer') {
            items = items.slice(0, 2);
        }

        if (items.length > 0) {
            items.forEach(item => {
                const listItem = this._createItemElement(category, item);
                itemsContainer.appendChild(listItem);
            });
        }

        categorySection.appendChild(itemsContainer);

        return categorySection;
    }

    _getCategoryLabel(category) {
        const labels = {
            'decors': 'DECORS',
            'faqs': 'FAQ',
            'projecten': 'PROJECTEN',
            'blogs_en_meer': 'BLOGS EN MEER',
        };

        return labels[category] || category.toUpperCase();
    }

    // Helper method to get the CSS class category name
    // Returns 'blogs' for 'blogs_en_meer' category, otherwise returns the category unchanged
    _getCssClassCategory(category) {
        return category === 'blogs_en_meer' ? 'blogs' : category;
    }

    _createItemElement(category, item) {
        const listItem = document.createElement('div');
        listItem.className = `c-live-search__item c-live-search__category__${this._getCssClassCategory(category)}-item`;

        switch(category) {
            case 'decors':
                listItem.innerHTML = `
                    <div class="c-live-search__category__item-thumbnail">
                         <a href="${item.url}">
                            ${item.image ? `<img src="${item.image}" ${item.srcset ? `srcset="${item.srcset}"` : ''} alt="${item.title}">` : ''}
                         </a>
                    </div>
                    <div class="c-live-search__category__item-content">
                        <a href="${item.url}" class="c-live-search__category__item-content__link">
                            <div>${item.title}</div>
                        </a>
                    </div>
                `;
                break;

            case 'faqs':
                listItem.innerHTML = `
                    <a href="/over-ons/veelgestelde-vragen/#faq${item.id}"  class="c-live-search__item-link" onclick="window.liveSearchInstance.closeSearchModal(); return true;">
                        <div class="c-live-search__category__item-title">${item.title}</div>
                        <div><img src="${themeVars.chevronRight}" alt="Chevron right"></div>
                    </a href="/over-ons/veelgestelde-vragen/#faq${item.id}">
                `;
                break;

            case 'projecten':
                listItem.innerHTML = `
                    <a href="${item.url}" class="c-live-search__item-link">
                        <div class="c-live-search__category__item-thumbnail c-live-search__category__item-thumbnail--projecten-blogs">
                             ${item.image ? `<img src="${item.image}" ${item.srcset ? `srcset="${item.srcset}"` : ''} alt="${item.title}">` : ''}
                        </div>
                        <div class="c-live-search__category__item-excerpt">${item.title || ''}</div>
                    </a>
                `;
                break;
            case 'blogs_en_meer':
                listItem.innerHTML = `
                    <a href="${item.url}" class="c-live-search__item-link">
                        <div class="c-live-search__category__item-thumbnail c-live-search__category__item-thumbnail--projecten-blogs">
                            ${item.image ? `<img src="${item.image}" ${item.srcset ? `srcset="${item.srcset}"` : ''} alt="${item.title}">` : ''}
                        </div>
                        <div class="c-live-search__category__item-excerpt">${item.title || ''}</div>
                    </a>
                `;
                break;

            default:
                listItem.innerHTML = `
                    <a href="${item.url}" class="c-live-search__item-link">
                        <div class="c-live-search__item-title">${item.title}</div>
                    </a>
                `;
        }

        return listItem;
    }
}

// LiveSearchModal handles the modal UI and user interactions
class LiveSearchModal {
    constructor() {
        this.modal = document.querySelector('.js-live-search-modal');
        this.originalModal = document.querySelector('.l-modal-screen--search');
        this.closeButton = document.querySelector('.js-close-live-search-btn');
    }

    open() {
        if (this.originalModal) {
            this.originalModal.classList.remove('open');
        }

        if (this.modal) {
            this.modal.classList.add('open');
            document.body.classList.add('no-scroll');
        }
    }

    close() {
        if (this.modal) {
            this.modal.classList.remove('open');
            document.body.classList.remove('no-scroll');
        }
    }

    showLoading() {
        const container = document.querySelector('.c-live-search__container');
        if (container) {
            container.classList.add('is-loading');
        }
    }

    hideLoading() {
        const container = document.querySelector('.c-live-search__container');
        if (container) {
            container.classList.remove('is-loading');
        }
    }
}

// Main LiveSearch class that coordinates everything
class LiveSearch {
    constructor() {
        this.searchInput = document.querySelector('.js-live-search-input');

        if (this.searchInput) {
            this.searchInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    const query = this.searchInput.value.trim();
                    if (query.length > 0) {
                        window.location.href = `${window.location.origin}/?s=${encodeURIComponent(query)}`;
                    }
                }
            });
        }

        this.resultsContainer = document.querySelector('.js-live-search-results');
        this.searchTriggers = document.querySelectorAll('.js-search-btn');

        this.minChars = 2;
        this.typingTimer = null;
        this.typingInterval = 300;
        this.currentSuggestion = null;

        this.api = new SearchAPI();
        this.modal = new LiveSearchModal();
        this.renderer = new ResultsRenderer(this.resultsContainer);

        window.liveSearchInstance = this;

        this.init();
    }

    init() {
        this.overrideOriginalSearchHandlers();

        if (this.searchInput) {
            this.searchInput.addEventListener('input', this.handleInput.bind(this));
            this.searchInput.addEventListener('focus', this.openSearchModal.bind(this));
        }

        const closeButton = document.querySelector('.js-close-live-search-btn');
        if (closeButton) {
            closeButton.addEventListener('click', this.closeSearchModal.bind(this));
        }

        document.addEventListener('click', (e) => {
            const modal = document.querySelector('.js-live-search-modal');
            if (modal && modal.classList.contains('open')) {
                if (!modal.contains(e.target) && !e.target.classList.contains('js-search-btn')) {
                    this.closeSearchModal();
                }
            }
        });

        document.addEventListener('keydown', (e) => {
            const modal = document.querySelector('.js-live-search-modal');
            if (e.key === 'Escape' && modal && modal.classList.contains('open')) {
                this.closeSearchModal();
            }
        });

        // GA4 tracking: capture full search term when clicking a livesearch result
        document.addEventListener('click', (e) => {
            const link = e.target.closest('a');
            if (!link) return;

            const isResultItem = !!(link.closest('.c-live-search__item') || link.closest('.c-live-search__category__item'));
            if (!isResultItem) return;

            if (link.closest('.c-live-search__category__header') || link.classList.contains('c-live-search__view-all-link')) {
                return;
            }

            const termFromInput = this.searchInput ? this.searchInput.value.trim() : '';
            const termFromRenderer = this.renderer && this.renderer.currentQuery ? this.renderer.currentQuery : '';
            const searchTerm = termFromInput || termFromRenderer;

            if (searchTerm) {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'livesearch_result_click',
                    search_term: searchTerm
                });

                if (typeof window.gtag === 'function') {
                    window.gtag('event', 'livesearch_result_click', { search_term: searchTerm });
                }
            }
        });
    }

    overrideOriginalSearchHandlers() {
        this.searchTriggers.forEach(trigger => {
            const newButton = trigger.cloneNode(true);
            trigger.parentNode.replaceChild(newButton, trigger);

            newButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.openSearchModal();
                return false;
            });
        });

        if (typeof jQuery !== 'undefined') {
            jQuery('.js-search-btn').off('click');

            jQuery(document).on('click', '.js-search-btn', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.openSearchModal();
                return false;
            });
        }
    }

    handleInput(e) {
        clearTimeout(this.typingTimer);

        const query = e.target.value.trim();

        if (query.length >= this.minChars) {
            this.typingTimer = setTimeout(() => {
                this.checkForCollectionSuggestions(query);
                this.search(query);
            }, this.typingInterval);
        } else if (query.length >= 1) {
            this.typingTimer = setTimeout(() => {
                this.checkForCollectionSuggestions(query);
            }, this.typingInterval);
        } else {
            this.renderer.clearResults();
            this.currentSuggestion = null;
        }
    }

    search(query) {
        this.modal.showLoading();

        // Check for collection suggestions when search is triggered directly
        this.checkForCollectionSuggestions(query);

        this.api.performSearch(query)
            .then(results => {
                this.renderer.clearResults();

                if (this.currentSuggestion) {
                    this.renderer.renderSuggestion(this.currentSuggestion);
                }

                this.renderer.renderResults(results, query);
            })
            .catch(error => {
                console.error('Error fetching search results:', error);
                this.renderer.clearResults();
                this.renderer.renderNoResults(query);
            })
            .finally(() => {
                this.modal.hideLoading();
            });
    }

    checkForCollectionSuggestions(query) {
        this.api.getSuggestions(query)
            .then(data => {
                if (!data.suggestion) {
                    this.currentSuggestion = null;
                    this.renderer.removeSuggestion();
                    return;
                }

                // Check if the suggestion is the same as the current query
                const isSameSuggestion = data.suggestion.name.toLowerCase() === query.toLowerCase();

                if (isSameSuggestion) {
                    // Don't show suggestion that matches the current query
                    this.currentSuggestion = null;
                    this.renderer.removeSuggestion();
                    return;
                }

                // We have a valid suggestion that is different from the current query
                this.currentSuggestion = data.suggestion;

                const hasResultsContainer = this.resultsContainer &&
                    this.resultsContainer.querySelector('.c-live-search__content-container');


                if (!hasResultsContainer) {
                    this.renderer.clearResults();
                }

                this.renderer.renderSuggestion(data.suggestion);
            })
            .catch(error => {
                console.error('Error fetching collection suggestions:', error);
            });
    }

    openSearchModal() {
        this.modal.open();

        if (this.searchInput) {
            setTimeout(() => {
                this.searchInput.focus();
            }, 100);
        }
    }

    closeSearchModal() {
        this.modal.close();

        if (this.searchInput) {
            this.searchInput.value = '';
            this.renderer.clearResults();
            this.currentSuggestion = null;
        }
    }
}

// Initialize the LiveSearch component when the DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
        new LiveSearch();
    }, 100);
});
